<template>
  <div class="content mb-5">
    <h2 class="mb-4">{{ this.$t("Filtering Options") }}</h2>
    <h3 class="mb-3">{{ this.$t("Safe Search") }}</h3>
    <p class="mb-3">
      {{
        this.$t(
          "Safe Search filters out explicit or potentially offensive search results and helps avoid most adult content search results"
        )
      }}
    </p>
    <b-row class="mb-5">
      <b-col sm="12" md="6" lg="6">
        <t-checkbox
          white-bg
          data-test-id="global-default-policy-safe-search"
          v-if="!loading"
          :label="this.$t('Safe Search')"
          :value-true="this.$t('ON')"
          :value-false="this.$t('OFF')"
          v-model="$v.form.safesearch.$model"
          id="safe_search"
        />
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <h3 class="mb-3">{{ this.$t("Unclassified Traffic") }}</h3>
    <p class="mb-3">
      {{
        this.$t(
          "Unclassified traffic is from websites that are new and are not yet assigned to a category in the central web categories database"
        )
      }}
    </p>
    <b-row class="mb-6">
      <b-col sm="12" md="6" lg="6">
        <t-checkbox
          white-bg
          data-test-id="global-default-policy-unclassified-traffic"
          v-if="!loading"
          :label="this.$t('Allow Unclassified Traffic')"
          :value-true="true"
          :value-false="false"
          v-model="$v.form.allowunclassified.$model"
          id="unclassified_traffic"
        />
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <h2 class="mb-3">{{ this.$t("Content Filtering") }}</h2>
    <p class="mb-3">
      {{
        this.$t("Choose a level of filtering to apply to your default policy.")
      }}
    </p>
    <b-row class="mb-4">
      <b-col sm="12">
        <t-radio-group
          v-model="form.level"
          :loading="loading"
          :items="contentFiltering"
          :edit-text="$t('View')"
          @change="$v.$touch()"
        />
      </b-col>
    </b-row>

    <t-save-button
      data-test-id="global-default-policy-save"
      :form-model="$v.form"
      :fetching="fetching"
      @submit="onSubmit"
    />
  </div>
</template>

<route>
{
  "name": "System Global Policy"
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  data: function () {
    return {
      loading: true,
      fetching: false,
      form: {
        safesearch: null,
        allowunclassified: null,
        emailnotofications: null,
        level: "M",
      },
      contentFiltering: [
        {
          text: this.$t("Essential"),
          subtitle: this.$t("Essential security protection."),
          tooltip: this.$t(
            "Blocks these categories: Anonymizer, Compromised, Criminal Skills/Hacking, Hate Speech, Phishing/Fraud, Spam, Spyware and Malicious Sites."
          ),
          testID: "global-default-policy-low",
          value: "L",
          checked: false,
          icon: "shield",
          canCustomise: true,
          customiseFunction: () => this.customise("Essential"),
        },
        {
          text: this.$t("Enhanced"),
          subtitle: this.$t(
            "Enhanced security protection, plus the blocking of adult related content."
          ),
          tooltip: this.$t(
            "Blocks Essential categories, plus Download Sites, Illegal Drugs, Online Ads, Pornography/Sex, Violence."
          ),
          testID: "global-default-policy-moderate",
          value: "M",
          checked: true,
          icon: "shield-shaded",
          canCustomise: true,
          customiseFunction: () => this.customise("Enhanced"),
        },
        {
          text: this.$t("Optimum"),
          subtitle: this.$t(
            "Enhanced security protection, blocking of adult related content, plus blocking of productivity inhibiting sites."
          ),
          tooltip: this.$t(
            "Blocks Essential and Enhanced categories, plus Alcohol, Dating, Gambling, Nudity, Weapons."
          ),
          testID: "global-default-policy-high",
          value: "H",
          checked: false,
          icon: "shield-fill",
          canCustomise: true,
          customiseFunction: () => this.customise("Optimum"),
        },
      ],
    };
  },
  validations: {
    form: {
      safesearch: {},
      emailnotofications: {},
      allowunclassified: {},
      level: {},
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("defaultPolicy", [
      "defaultCategories",
      "defaultSpecificCategory",
      "defaultFilteringPolicy",
      "lowLevelCategories",
      "moderateLevelCategories",
      "highLevelCategories",
    ]),
    ...mapGetters("categories", ["customerCategories"]),
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("defaultPolicy", [
      "fetchDefaultPolicyCategories",
      "fetchDefaultSpecificPolicyCategories",
      "fetchDefaultFilteringPolicy",
      "updateDefaultPolicyCategories",
      "updateDefaultSpecificCategory",
      "updateDefaultFilteringPolicy",
    ]),
    ...mapActions("categories", ["fetchPolicyCategories"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    customise(level) {
      this.createModal({
        modal: "t-categories",
        dataTestId: "modal-categories",
        title: this.$t("Categories"),
        subtitle: this.$t("Level") + ": " + this.$t(level),
        modalData: {
          policy: this.defaultFilteringPolicy,
          categories: this.getCategories(level),
          readonly: true,
          isSystem: true,
        },
        props: {
          size: "xl",
          okTitle: this.$t("Close"),
          okOnly: true,
        },
      });

      this.showModal();
    },
    getCategories(level) {
      switch (level) {
        case "Essential":
          return this.lowLevelCategories;
        case "Enhanced":
          return this.moderateLevelCategories;
        case "Optimum":
          return this.highLevelCategories;
        default:
          return this.defaultCategories;
      }
    },
    async onSubmit() {
      this.fetching = true;
      try {
        await this.updateDefaultFilteringPolicy({
          defaultPolicy: {
            safesearch: this.form.safesearch,
            allowunclassified: this.form.allowunclassified,
            level: this.form.level,
          },
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },
  watch: {
    defaultFilteringPolicy() {
      this.form = this.defaultFilteringPolicy;

      this.setPageTitle(this.$t("Default Policy"));
      this.setPageSubtitle([
        this.$t(
          "Policies determine what categories of website your users have access to."
        ),
        this.$t(
          "Set the default policy that you wish to assign to your users when you add them to WebTitan Cloud."
        ),
      ]);
    },
  },
  async created() {
    try {
      await Promise.all([
        this.fetchDefaultFilteringPolicy({}),
        this.fetchDefaultPolicyCategories({}),
      ]);
    } finally {
      this.loading = false;
    }
  },
};
</script>
